import { BreadcrumbRouterLink } from '@/components/link/Link.tsx';
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator
} from '@/components/ui/breadcrumb.tsx';
import { ROUTES } from '@/constants.ts';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form.tsx';
import { Input } from '@/components/ui/input.tsx';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { GpsLockFromSchema, gpsLockSchema } from '@/routes/device/gps-lock/zodSchemas.ts';
import { Button } from '@/components/ui/button.tsx';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { getUIErrorMessage } from '@/utils/errors.ts';
import { useState } from 'react';
import { checkGpsLockByDeviceId } from '@/routes/device/gps-lock/constants.ts';
import Spinner from '@/components/spinner/Spinner.tsx';
import useTitle from '@/hooks/useTitle.ts';

function GpsLock() {
	useTitle('GPS Lock');

	const [error, setError] = useState('');
	const [gpsLock, setGpsLock] = useState(undefined);

	const gpsLockForm = useForm<GpsLockFromSchema>({
		resolver: zodResolver(gpsLockSchema)
	});

	const {
		isPending: isPendingCheck,
		mutate: checkGpsLock,
		isSuccess: isCheckSuccess
	} = useMutation({
		mutationFn: checkGpsLockByDeviceId,
		onSuccess: response => {
			setGpsLock(response.data.gpsLock);
			toast('Check successfully');
		},
		onError: error => {
			setError(getUIErrorMessage(error));
			toast.error(getUIErrorMessage(error));
		}
	});

	const handleCheckGpsLock = gpsLockForm.handleSubmit(values => {
		return checkGpsLock(values.deviceId);
	});

	return (
		<div className="flex flex-col gap-10">
			<Breadcrumb>
				<BreadcrumbList>
					<BreadcrumbItem>
						<BreadcrumbRouterLink to={ROUTES.home}>Home</BreadcrumbRouterLink>
					</BreadcrumbItem>
					<BreadcrumbSeparator />
					<BreadcrumbItem>
						<BreadcrumbPage>GPS lock</BreadcrumbPage>
					</BreadcrumbItem>
				</BreadcrumbList>
			</Breadcrumb>

			<Form {...gpsLockForm}>
				<div className="flex justify-center items-center">
					<div className="w-full max-w-sm flex flex-col gap-10">
						<div className="grid grid-cols-1 gap-x-4 gap-y-3 items-start">
							<FormField
								control={gpsLockForm.control}
								name="deviceId"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Device ID</FormLabel>
										<FormControl>
											<Input
												type="number"
												className="input"
												onChange={e => field.onChange(e.target.valueAsNumber)}
												name={field.name}
												value={field.value}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<Button type="submit" onClick={() => handleCheckGpsLock()}>
							{error ? 'Try again ' : isPendingCheck ? <Spinner className="ml-2" /> : 'Check'}
						</Button>
						{isCheckSuccess && (
							<div className="flex flex-row text-sm text-gray-800 dark:text-gray-200 gap-2">
								<span className="font-medium">GPS Lock:</span>
								<span className={`font-semibold ${gpsLock ? 'text-green-600' : 'text-red-600'}`}>
									{gpsLock ? 'OK' : 'No'}
								</span>
							</div>
						)}
					</div>
				</div>
			</Form>
		</div>
	);
}

export default GpsLock;
